import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getBlogArticles } from "@/lib/api";

const BlogListPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await getBlogArticles();
        setArticles(data.articles);
      } catch (error) {
        console.error("Error fetching blog articles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="h-10 w-64 bg-gray-200 animate-pulse rounded mb-8" />
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-gray-200 animate-pulse rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="blog-list-page">
      <SeoHead
        title="Beach News & Guides"
        description="Stay informed with the latest beach access news, guides, safety tips, and state-by-state beach access rankings across the United States."
        path="/blog"
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 font-['Outfit'] tracking-tight">
            Beach News & Guides
          </h1>
          <p className="mt-3 text-gray-600 text-base sm:text-lg max-w-2xl">
            The latest on public beach access rights, safety tips, and destination guides across the US.
          </p>
        </div>

        <div className="space-y-6">
          {articles.map((article, idx) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="group block bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              data-testid={`blog-card-${idx}`}
            >
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-72 lg:w-80 flex-shrink-0">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-48 sm:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                        <Tag className="w-3 h-3" />
                        {article.category}
                      </span>
                      {article.state && (
                        <span className="text-xs text-gray-500 font-medium">{article.state}</span>
                      )}
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-sky-600 transition-colors font-['Outfit'] mb-2">
                      {article.title}
                    </h2>
                    <p className="text-gray-600 text-sm line-clamp-2">{article.summary}</p>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(article.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {article.read_time}
                      </span>
                    </div>
                    <span className="flex items-center gap-1 text-sky-600 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogListPage;
